/**
 * Zoom da interface (2026-08-27).
 *
 * Zoom PROPRIO do app — CSS `zoom` na raiz — em vez do zoom nativo do
 * navegador/webview. Diferenca que o usuario pediu: o conteudo REFLUI (nao
 * fica fixo nem cortado) e a proporcao segue igual ao navegar, porque a SPA
 * nao recarrega e o valor fica na raiz do documento.
 *
 * - Zoom PADRAO (Preferencias): o que vale ao CONECTAR; 100% por padrao.
 * - Ctrl +/- e Ctrl+scroll ajustam o zoom vivo durante a sessao.
 * - Ctrl+0 volta ao padrao.
 *
 * SO no Orbind Desktop: no navegador comum o proprio browser ja faz o zoom
 * (reflui, persiste, Ctrl +/-), entao aqui seria zoom em cima de zoom. No
 * desktop, este CSS substitui o zoom nativo do webview que deixava a area
 * "fixa" (nao refluia) — o pedido do usuario.
 */

import { isDesktop } from './platform.ts'

const CHAVE_PADRAO = 'orbind.zoom.default'
export const ZOOM_MIN = 50
export const ZOOM_MAX = 200
export const ZOOM_PASSO = 10

/** Arredonda para o passo e prende no intervalo permitido. */
export function normalizarZoom(pct: number): number {
  const passo = Math.round(pct / ZOOM_PASSO) * ZOOM_PASSO
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, passo))
}

function lerPadrao(): number {
  try {
    const v = Number(localStorage.getItem(CHAVE_PADRAO))
    return Number.isFinite(v) && v > 0 ? normalizarZoom(v) : 100
  } catch {
    return 100
  }
}

let atual = 100
const ouvintes = new Set<(pct: number) => void>()

function notificar() {
  for (const f of ouvintes) f(atual)
}

function aplicar(pct: number) {
  // So mexe na raiz no DESKTOP; no navegador o zoom e do proprio browser.
  if (!isDesktop()) return
  // CSS `zoom` na raiz: reflui o layout (diferente de transform: scale, que
  // so escala e deixa o conteudo do mesmo tamanho logico).
  if (pct === 100) document.documentElement.style.removeProperty('zoom')
  else document.documentElement.style.setProperty('zoom', String(pct / 100))
}

/** Zoom PADRAO configurado (o que vale ao conectar). */
export function zoomPadrao(): number {
  return lerPadrao()
}

/** Zoom vivo aplicado agora. */
export function zoomAtual(): number {
  return atual
}

/** Define o zoom PADRAO (Preferencias) e aplica na hora. Retorna o valor final. */
export function definirZoomPadrao(pct: number): number {
  const v = normalizarZoom(pct)
  try {
    localStorage.setItem(CHAVE_PADRAO, String(v))
  } catch {
    /* sem persistencia: vale so nesta sessao */
  }
  atual = v
  aplicar(v)
  notificar()
  return v
}

/** Ajusta o zoom VIVO da sessao (nao mexe no padrao). */
export function definirZoomVivo(pct: number): number {
  atual = normalizarZoom(pct)
  aplicar(atual)
  notificar()
  return atual
}

export function ajustarZoom(delta: number) {
  definirZoomVivo(atual + delta)
}

/** Volta o zoom vivo ao padrao configurado. */
export function restaurarZoom() {
  definirZoomVivo(zoomPadrao())
}

/** Assina mudancas do zoom (para a UI refletir). Dispara o valor atual na hora. */
export function assinarZoom(f: (pct: number) => void): () => void {
  ouvintes.add(f)
  f(atual)
  return () => {
    ouvintes.delete(f)
  }
}

/**
 * Aplica o padrao ao CONECTAR e liga os atalhos (Ctrl +/-/0 e Ctrl+scroll).
 * Chamar UMA vez no boot. Os atalhos usam preventDefault para substituir o
 * zoom nativo do webview — assim so existe um zoom, o que reflui.
 */
export function iniciarZoom() {
  // No navegador nao faz nada: o zoom e nativo do browser (evita zoom duplo).
  if (!isDesktop()) return
  atual = lerPadrao()
  aplicar(atual)

  window.addEventListener(
    'keydown',
    (e) => {
      if (!(e.ctrlKey || e.metaKey)) return
      if (e.key === '=' || e.key === '+') {
        e.preventDefault()
        ajustarZoom(ZOOM_PASSO)
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault()
        ajustarZoom(-ZOOM_PASSO)
      } else if (e.key === '0') {
        e.preventDefault()
        restaurarZoom()
      }
    },
    { passive: false },
  )

  window.addEventListener(
    'wheel',
    (e) => {
      if (!(e.ctrlKey || e.metaKey)) return
      e.preventDefault()
      ajustarZoom(e.deltaY < 0 ? ZOOM_PASSO : -ZOOM_PASSO)
    },
    { passive: false },
  )
}
